import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LazyImage from '../LazyImage/LazyImage';
import { resolveImageUrl } from '../../utils/imageUrl';
import { buildSubscriberSignInPath, isSubscriberOnlyContent } from '../../utils/subscriberAccess';
import './TechCard.css';

const TechCard = ({ tech, variant = 'default' }) => {
  const { isAuthenticated } = useAuth();
  if (!tech) return null;

  // Get the cover image URL for tech articles
  const getTechImageUrl = (tech) => {
    const imagePath = tech.cover_image_url || tech.cover_image;
    return resolveImageUrl(imagePath);
  };

  const techImageUrl = getTechImageUrl(tech);

  // Function to strip HTML tags from content
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Use excerpt if available, otherwise truncate body
  const rawDescription = tech.excerpt || tech.body || '';
  const description = rawDescription
    ? (stripHtml(rawDescription).length > 150
        ? `${stripHtml(rawDescription).substring(0, 150)}...`
        : stripHtml(rawDescription))
    : '';

  // Calculate reading time
  const readingTime = tech.reading_time || Math.ceil((tech.body?.length || 0) / 1000);

  // Format date
  const techDate = new Date(tech.published_at || tech.created_at);
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format view count
  const formatViewCount = (count) => {
    if (!count) return '0 views';
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M views`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K views`;
    return `${count} views`;
  };

  const isLocked = isSubscriberOnlyContent(tech);
  const detailPath = `/tech/${tech.slug}`;
  const destination =
    isLocked && !isAuthenticated
      ? buildSubscriberSignInPath(detailPath)
      : detailPath;
  const cardClasses = `tech-card ${variant} ${tech.is_featured ? 'featured' : ''} ${tech.is_sticky ? 'sticky' : ''} ${isLocked ? 'featured' : ''}`;

  return (
    <article className={cardClasses}>
      <Link to={destination} className="tech-card-link">
        {/* Image Section */}
        {techImageUrl && (
          <div className="tech-card-image">
            <LazyImage
              src={techImageUrl}
              alt={tech.title}
              className="tech-image"
              loading="lazy"
            />
            {tech.is_featured && (
              <span className="tech-badge featured-badge">
                <i className="fas fa-star"></i>
                Featured
              </span>
            )}
            {isLocked && (
              <span className="tech-badge sticky-badge" title="Premium member content" aria-label="Premium member content">
                <i className="fas fa-crown"></i>
              </span>
            )}
            {tech.is_sticky && (
              <span className="tech-badge sticky-badge">
                <i className="fas fa-thumbtack"></i>
                Pinned
              </span>
            )}
          </div>
        )}

        {/* Content Section */}
        <div className="tech-card-content">
          {/* Tags */}
          {tech.tags && tech.tags.length > 0 && (
            <div className="tech-tags">
              {tech.tags.slice(0, 3).map((tag, index) => (
                <span key={index} className="tech-tag">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Title */}
          <h3 className="tech-card-title">{tech.title}</h3>

          {/* Description */}
          {description && (
            <p className="tech-card-description">{description}</p>
          )}

          {/* Meta Info */}
          <div className="tech-card-meta">
            <div className="tech-meta-left">
              <span className="tech-date">
                <i className="far fa-calendar-alt"></i>
                {formatDate(techDate)}
              </span>
              <span className="tech-reading-time">
                <i className="far fa-clock"></i>
                {readingTime} min read
              </span>
            </div>
            <div className="tech-meta-right">
              {tech.view_count > 0 && (
                <span className="tech-views">
                  <i className="far fa-eye"></i>
                  {formatViewCount(tech.view_count)}
                </span>
              )}
            </div>
          </div>

          {/* Author */}
          {tech.user && (
            <div className="tech-card-author">
              <span className="author-name">
                <i className="far fa-user"></i>
                {tech.user.name}
              </span>
            </div>
          )}
        </div>
      </Link>
    </article>
  );
};

export default TechCard;
